import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  signal,
} from '@angular/core';
import {
  FormValueControl,
  ValidationError,
  WithOptionalFieldTree,
} from '@angular/forms/signals';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'lib-ui-password-input',
  imports: [CommonModule, FontAwesomeModule],
  template: `
    <div class="relative">
      <input
        id="password"
        [attr.autocomplete]="autocomplete()"
        [type]="showPassword() ? 'text' : 'password'"
        [value]="value()"
        (input)="updatePassword($any($event.target).value)"
        (focus)="showMeter.set(true)"
        (blur)="onBlur()"
        [readonly]="readonly()"
        [disabled]="disabled()"
        class="form-control w-full"
        [class.form-control-valid]="!invalid()"
        [class.form-control-error]="invalid() && touched()"
      />

      <!-- Eye Icon -->
      <button
        type="button"
        (click)="toggleShow()"
        class="absolute right-3 top-2.5 text-gray-500 dark:text-gray-400 text-xs cursor-pointer"
        tabindex="-1"
      >
        <fa-icon [icon]="showPassword() ? faEyeSlash : faEye"></fa-icon>
      </button>

      <!-- Dropdown Meter Panel -->
      @if (showMeter()) {
        <div
          class="absolute z-10 top-[calc(100%+6px)] left-0 w-full p-3 bg-white dark:bg-[#1e1e2c] border border-gray-300 dark:border-gray-700 rounded-md shadow-lg text-xs"
        >
          <div class="text-gray-700 dark:text-gray-300 mb-1">
            Password must contain:
          </div>
          <ul class="pl-4 space-y-0.5 mb-2">
            <li [ngClass]="hasMinLength() ? 'text-green-600' : 'text-red-500'">
              At least 8 characters
            </li>
            <li [ngClass]="hasUpperCase() ? 'text-green-600' : 'text-red-500'">
              An uppercase letter
            </li>
            <li [ngClass]="hasLowerCase() ? 'text-green-600' : 'text-red-500'">
              A lowercase letter
            </li>
            <li [ngClass]="hasNumber() ? 'text-green-600' : 'text-red-500'">
              A number
            </li>
            <li [ngClass]="hasSpecial() ? 'text-green-600' : 'text-red-500'">
              A special character
            </li>
          </ul>
          <div class="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              class="h-full transition-all duration-300"
              [ngClass]="strengthColor()"
              [style.width.%]="(strength() / 5) * 100"
            ></div>
          </div>
          <div class="text-xs mt-1 text-gray-700 dark:text-gray-300">
            <strong>{{ strengthLabel() }}</strong>
          </div>
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordInput implements FormValueControl<string> {
  protected readonly faEye = faEye;
  protected readonly faEyeSlash = faEyeSlash;

  autocomplete = input<string>('new-password');
  readonly = input<boolean>(false);
  disabled = input<boolean>(false);
  invalid = input<boolean>(false);
  errors = input<readonly WithOptionalFieldTree<ValidationError>[]>([]);

  value = model<string>('');
  touched = model<boolean>(false);

  showPassword = signal(false);
  showMeter = signal(false);

  // Reactive validation conditions
  hasMinLength = computed(() => this.value().length >= 8);
  hasUpperCase = computed(() => /[A-Z]/.test(this.value()));
  hasLowerCase = computed(() => /[a-z]/.test(this.value()));
  hasNumber = computed(() => /\d/.test(this.value()));
  hasSpecial = computed(() => /[^A-Za-z0-9]/.test(this.value()));

  strength = computed(() => {
    let score = 0;
    if (this.hasMinLength()) score++;
    if (this.hasUpperCase()) score++;
    if (this.hasLowerCase()) score++;
    if (this.hasNumber()) score++;
    if (this.hasSpecial()) score++;
    return score;
  });

  strengthColor = computed(() => {
    const score = this.strength();
    if (score <= 2) return 'bg-red-500';
    if (score <= 4) return 'bg-yellow-500';
    return 'bg-green-600';
  });

  strengthLabel = computed(() => {
    const score = this.strength();
    if (score <= 2) return 'Weak';
    if (score <= 4) return 'Moderate';
    return 'Strong';
  });

  onBlur() {
    this.touched.set(true);
    setTimeout(() => {
      this.showMeter.set(false);
    }, 150);
  }

  updatePassword(value: string) {
    this.value.set(value);
    this.showMeter.set(true);
  }

  toggleShow() {
    this.showPassword.update((v) => !v);
  }

  validate() {
    const errors: { message: string }[] = [];
    if (!this.hasMinLength()) errors.push({ message: 'At least 8 characters' });
    if (!this.hasUpperCase())
      errors.push({ message: 'At least one uppercase letter' });
    if (!this.hasLowerCase())
      errors.push({ message: 'At least one lowercase letter' });
    if (!this.hasNumber()) errors.push({ message: 'At least one number' });
    if (!this.hasSpecial())
      errors.push({ message: 'At least one special character' });

    return errors.length > 0 ? errors : null;
  }
}
